import React, { useState } from 'react';

import { useMediaQuery, useTheme } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';

import { UiFilterTextField } from '../../ui/ui-filter-text-field';
import { ApplicantSourceStatus } from '../applicant-source-status/applicant-source-status';

import { JobDropdownOption } from './job-dropdown-option';

import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

export type JobSelectorOption = {
  label: string;
  value: string;
  optionGroup: string;
  jobStatus?: JobStatus;
};

type JobSelectorOptionMap = {
  [location: string]: JobSelectorOption[];
};

const NON_LOCATION_GROUPS = ['Job Status', 'orgDefault'];

const FirstJobOption = ({
  selectedOption,
  maxWidth,
  isOnMobile,
}: {
  selectedOption: JobSelectorOption;
  maxWidth: number;
  isOnMobile: boolean;
}) => {
  const formattedJobTitle = NON_LOCATION_GROUPS.includes(selectedOption.optionGroup)
    ? selectedOption.label
    : `${selectedOption.label} (${selectedOption.optionGroup})`;

  return (
    <div className={`typo-headerLink--large u-spread u-alignItems-center u-maxWidth-${maxWidth}px`}>
      <Tooltip title={formattedJobTitle} arrow enterDelay={700} enterNextDelay={700}>
        <div className="u-width-95 u-textWrap-nowrap u-textOverflow-ellipsis u-overflow-hidden">
          {formattedJobTitle}
        </div>
      </Tooltip>
      <MaterialDesignIcon
        iconName={ICON_NAMES.EXPAND_MORE}
        className={isOnMobile ? '' : 'u-margin-left'}
      />
    </div>
  );
};

const JobDropdownLocationSubheader = ({ title }: { title: string }) =>
  NON_LOCATION_GROUPS.includes(title) ? null : (
    <div className="typo-subheading u-width-100 u-padding u-padding-bottom-xs">{title}</div>
  );

type InputProps = {
  orgId: number;
  selectedJob?: Job;
  includeStatusIndicator?: boolean;
  selectedOption: JobSelectorOption;
  /** Filter options are all searchable job options */
  filterOptions: JobSelectorOption[];
  /** All options includes unsearchable options for each use case - such as "Organization Defaults" or "All Hiring" */
  allOptions: JobSelectorOption[];
  handleChange: (o: JobSelectorOption) => void;
};

export const JobDropdownBase = ({
  orgId,
  selectedJob,
  includeStatusIndicator,
  selectedOption,
  filterOptions,
  allOptions,
  handleChange,
}: InputProps) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isOnMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const menuMaxHeight = menuOpen ? 450 : 0;
  const menuWidth = isSmallScreen ? (isOnMobile ? 250 : 300) : 450;
  const maxWidth = menuWidth + 50;
  const titleMaxWidth = isOnMobile ? 200 : menuWidth;

  const handleCloseMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleChangeSearchTerm = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm || '');
  };

  const optionsToDisplay = searchTerm
    ? filterOptions.filter((o) => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : allOptions;

  const displayOptionsByLocation = optionsToDisplay.reduce((map, option) => {
    const jobLocation = option.optionGroup;

    if (map[jobLocation]) {
      map[jobLocation].push(option);
    } else {
      map[jobLocation] = [option];
    }

    return map;
  }, {} as JobSelectorOptionMap);

  const handleClick = (jobOption: JobSelectorOption) => () => {
    handleChange(jobOption);
    toggleMenu();
  };

  return (
    <ClickAwayListener onClickAway={handleCloseMenu}>
      <div>
        <div
          className={`u-display-flex u-alignItems-center u-textAlign-center u-maxWidth-${maxWidth}px`}
        >
          {selectedJob && includeStatusIndicator && (
            <ApplicantSourceStatus job={selectedJob} orgId={orgId} iconEdge="start" />
          )}
          <div className="u-cursor-pointer" role="presentation" onClick={toggleMenu}>
            <FirstJobOption
              selectedOption={selectedOption}
              maxWidth={titleMaxWidth}
              isOnMobile={isOnMobile}
            />
          </div>
        </div>
        {menuOpen ? (
          <div
            className="u-border u-background-white u-color-black u-position-absolute"
            style={{ zIndex: 1, width: menuWidth, marginLeft: '-4px' }}
          >
            <div className="u-elevation-3">
              <div className="u-padding-y-md u-border-bottom">
                <UiFilterTextField
                  className="u-padding-sm"
                  placeholder="Search by Job Name"
                  label=""
                  value={searchTerm}
                  onChange={handleChangeSearchTerm}
                  startAdornment={
                    <InputAdornment position="start">
                      <MaterialDesignIcon iconName={ICON_NAMES.SEARCH} size="sm" />
                    </InputAdornment>
                  }
                  fullWidth
                />
              </div>
              <div className="u-overflowY-scroll" style={{ maxHeight: menuMaxHeight }}>
                {Object.keys(displayOptionsByLocation).map((jobLocation) => (
                  <div key={`job-location-${jobLocation}`}>
                    <JobDropdownLocationSubheader title={jobLocation} />
                    {displayOptionsByLocation[jobLocation].map((jobOption) => (
                      <div
                        key={jobOption.value}
                        role="presentation"
                        onClick={handleClick(jobOption)}
                      >
                        <JobDropdownOption
                          label={jobOption.label}
                          isSelected={selectedOption.value === jobOption.value}
                          jobStatus={jobOption.jobStatus}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </ClickAwayListener>
  );
};
